import { useEffect, useState } from "react";
import { fetchShopProducts, fetchShopSettings } from "./api";
import CoffeeCheckoutFlow from "./components/shop/CoffeeCheckoutFlow";
import LandingNav from "./components/layout/LandingNav";
import SiteFooter from "./components/ui/SiteFooter";
import { buildCartLines, useCart } from "./shop/cartStore";
import { translations } from "./translations";

function getCheckoutLang() {
  const lang = new URLSearchParams(window.location.search).get("lang");
  return lang === "en" ? "en" : "hu";
}

/** Webshop checkout: the coffee checkout steps, fed with the cart instead of one coffee. */
function CartCheckoutPage() {
  const [lang, setLang] = useState(getCheckoutLang);
  const [shop, setShop] = useState(null);
  const [products, setProducts] = useState(null);
  const { items } = useCart();
  const siteT = translations[lang];
  const t = siteT.checkout;
  const shopHref = `/webshop?lang=${lang}`;

  useEffect(() => {
    document.title = `${t.orderTitle} — Mizu Brunch & Coffee`;
    document.documentElement.lang = lang;
  }, [lang, t.orderTitle]);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const [shopData, productData] = await Promise.all([
          fetchShopSettings(),
          fetchShopProducts(),
        ]);
        if (cancelled) return;
        if (!productData.webshopEnabled) {
          window.location.replace(shopHref);
          return;
        }
        setShop(shopData);
        setProducts(productData.products || []);
      } catch {
        if (!cancelled) window.location.replace(shopHref);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
    // Load once; the language switch does not need fresh data.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const lines = products ? buildCartLines(items, products, lang) : [];
  const cartReady = Boolean(shop && products);
  const cartUsable = lines.length > 0 && lines.every((line) => line.available);

  useEffect(() => {
    // Empty or stale cart: back to the shop to fix it.
    if (cartReady && !cartUsable) window.location.replace(shopHref);
  }, [cartReady, cartUsable, shopHref]);

  function handleLangChange(nextLang) {
    setLang(nextLang);
    const url = new URL(window.location.href);
    url.searchParams.set("lang", nextLang);
    window.history.replaceState({}, "", url);
  }

  function handleSuccess() {
    window.location.href = `/checkout/confirm?status=success&from=shop&lang=${lang}`;
  }

  return (
    <div className="landing checkout-page">
      <LandingNav lang={lang} onLangChange={handleLangChange} t={siteT} hrefPrefix="/" />

      <main className="checkout-page-main">
        {cartReady && cartUsable ? (
          <CoffeeCheckoutFlow
            shop={shop}
            t={t}
            lang={lang}
            cartLines={lines}
            backHref={shopHref}
            backLabel={siteT.shop.backToShop}
            unavailableText={siteT.shop.productUnavailable}
            onSuccess={handleSuccess}
          />
        ) : (
          <p className="checkout-page-loading">{t.loading}</p>
        )}
      </main>

      <SiteFooter t={siteT.visitUs} />
    </div>
  );
}

export default CartCheckoutPage;
